import {
  AskIcon,
  MaterialIcon,
  SeedBibleIcon,
  StopIcon,
} from "../components/icons";
import type { JSX, VNode } from "preact";
import { computed, signal } from "@preact/signals";
import type { ReadonlySignal } from "@preact/signals";
import {
  DEFAULT_BOOK_ID,
  uiLocaleForDefaultTranslation,
  hasAnyDiscoverResults,
  type BibleReadingState,
  type BibleSelectedVerse,
} from "../managers/BibleReadingManager";
import { buildReadingUrl } from "../managers/ReadingUrlPath";
import { extractContentText } from "../managers/ChapterText";
import type { BookId } from "../managers/BibleDataManager";
import { readInjectedConfig, type BrandingConfig } from "../app/appConfig";
import type { PanesManager } from "../managers/PanesManager";
import type { TabSlot, TabsLayoutManager } from "../managers/TabsLayoutManager";
import {
  formatVerseSelection,
  type TabsManager,
} from "../managers/TabsManager";
import type { BibleSelectorState } from "../managers/BibleSelectorManager";
import { sortBy } from "es-toolkit";
import type { BibleReadingSession } from "../managers/SessionsManager";
import type { ChatsManager } from "./ChatsManager";
import type { ModalManager } from "./ModalManager";
import type { AppState } from "./SeedBibleStateManager";
import type { ReadingPlansManager } from "../managers/ReadingPlansManager";
import {
  ReadingPlansPane,
  ReadingPlansPaneActions,
  ReadingPlansPaneIcon,
  ReadingPlansPaneLeading,
  ReadingPlansPaneTitle,
} from "../components/ReadingPlansPane/ReadingPlansPane";
import {
  groupVersesIntoPlaylistItems,
  type PlaylistManager,
} from "./PlaylistManager";
import type { AnnotationsManager } from "./AnnotationsManager";
import { i18n, useI18n } from "../i18n";
import {
  FEATURE_KEY_READING_PLANS,
  type FeaturesManager,
} from "./FeaturesManager";
import { playlistItemLabel } from "../components/playlistItemLabel";
import { ShareModal } from "../components/ShareModal/shareModal";

type BibleToolIcon<TContext> = (context: TContext) => JSX.Element | VNode;
type ResolvedBibleToolIcon = () => JSX.Element | VNode;
type ToolPredicateResult = boolean | ReadonlySignal<boolean>;
type ToolPredicate<TContext> = (context: TContext) => ToolPredicateResult;
type ToolPriority<TContext> = number | ((context: TContext) => number);
export type TranslatableTitle =
  | string
  | {
      key: string;
      defaultValue: string;
      ns?: string;
      options?: Record<string, string>;
    };

/**
 * Base tool contract shared by all tool surfaces.
 */
export interface BibleTool<TContext> {
  /** Stable tool identifier used for registration/replacement. */
  id: string;
  /**
   * Sorting priority. Lower values render first.
   *
   * For extensions, this should be between 200 and 999 to appear after default tools, but before the previous chapter button.
   */
  priority: ToolPriority<TContext>;
  /** Localized or plain-text tool title. */
  title: TranslatableTitle;
  /** Icon renderer for the given tool context. */
  icon: BibleToolIcon<TContext>;
}

/**
 * A context-menu item that can be shown for a selected tool.
 *
 * Notes:
 * - Items intentionally do not define priority. Their order is preserved from
 *   the array returned by getItems().
 * - Items cannot define nested getItems() submenus.
 */
export interface ManagedBibleToolItem<TContext> extends Omit<
  BibleTool<TContext>,
  "priority"
> {
  /** Optional disabled predicate (boolean or signal). */
  isDisabled?: ToolPredicate<TContext>;
  /** Optional visibility predicate (boolean or signal). */
  isVisible?: ToolPredicate<TContext>;
  /** Optional action callback for item activation. */
  onSelect?: (context: TContext) => void;
  /** Nested menu items are not supported for context menu entries. */
  getItems?: never;
}

/**
 * Base resolved tool contract returned by tools manager getter methods.
 *
 * Unlike managed/registerable tools, resolved tools have fixed numeric priority
 * and no-arg icon renderers because context has already been applied.
 */
export interface ResolvedBibleTool {
  /** Stable tool identifier used for registration/replacement. */
  id: string;
  /** Resolved sorting priority. Lower values render first. */
  priority: number;
  /** Localized or plain-text tool title. */
  title: TranslatableTitle;
  /** Context-bound icon renderer. */
  icon: ResolvedBibleToolIcon;
}

/** Fully resolved context-menu item ready for rendering. */
export interface ResolvedBibleToolItem extends Omit<
  ResolvedBibleTool,
  "priority"
> {
  /** Disabled state signal resolved for current context. */
  disabled: ReadonlySignal<boolean>;
  /** Visibility state signal resolved for current context. */
  visible: ReadonlySignal<boolean>;
  /** Invoked when the user activates the menu item. */
  onSelect: () => void;
}

/** Window metrics provided to tools when available. */
export interface WindowContext {
  /**
   * Whether the app is currently being rendered in a mobile layout.
   */
  isMobile: boolean;
}

/** Runtime context passed to reader and verse toolbar tools. */
export interface BibleToolContext {
  /** Active reading state for current reader surface. */
  readingState: BibleReadingState;
  /**
   * The current shared bible reading session, if any.
   */
  sharedSession: BibleReadingSession | null;
  /** Bible selector state for opening navigation overlays. */
  selectorState: BibleSelectorState;
  /** Tabs manager for tab-level actions when needed by tools. */
  tabs: TabsManager;
  /** Panes manager for pane-level actions/selection context. */
  panesManager: PanesManager;
  /** Tabs layout manager for slot-level actions/selection context. */
  tabsLayoutManager: TabsLayoutManager;

  /**
   * Chats manager for chat-related actions.
   */
  chats: ChatsManager;

  /** Optional window metrics for responsive tool behavior. */
  window?: WindowContext | null;
  /** Opens the app sidebar (typically for small-screen actions). */
  openSidebar: () => void;
  /** Opens the search interface. */
  openSearch: () => void;
  /** Opens the chat / cross-references floating panel. */
  openChat?: () => void;
  /** Opens the discover panel */
  openDiscover?: () => void;
  /** Shows a transient toast message at the bottom of the screen. */
  toast: (message: string) => void;
  /** Reading plans manager, for opening the plans pane. */
  readingPlans?: ReadingPlansManager;
  /** Playlist manager */
  playlists?: PlaylistManager;

  /** Annotations manager, for creating/editing notes on selected verses. */
  annotations?: AnnotationsManager;

  /** Features manager */
  features: FeaturesManager;

  /** Modals manager */
  modals?: ModalManager;

  /**
   * App-level state. Optional like the other managers above; tools that need
   * shared-session actions (create/share the live session) should guard on it.
   */
  app?: AppState;
}

/** Fully resolved reader toolbar tool ready for rendering. */
export interface BibleReaderToolbarTool extends ResolvedBibleTool {
  isControllable: boolean;
  /** Disabled state signal resolved for current context. */
  disabled: ReadonlySignal<boolean>;
  /** Visibility state signal resolved for current context. */
  visible: ReadonlySignal<boolean>;
  /** Invoked when the user activates the tool. */
  onSelect: () => void;
  /** Optional context-menu items for this tool. */
  getItems?: () => ResolvedBibleToolItem[];

  /**
   * Whether the label for this tool should be hidden.
   * Defaults to false.
   */
  hideLabel?: boolean;
}

export type ManagedBibleToolbarToolItem =
  ManagedBibleToolItem<BibleToolContext>;

/** Registerable reader toolbar tool definition. */
export interface ManagedBibleToolbarTool extends BibleTool<BibleToolContext> {
  /** Whether the tool is controllable by the user. */
  isControllable?: boolean;
  /** Optional disabled predicate (boolean or signal). */
  isDisabled?: ToolPredicate<BibleToolContext>;
  /** Optional visibility predicate (boolean or signal). */
  isVisible?: ToolPredicate<BibleToolContext>;
  /** Optional action callback for tool activation. Mutually exclusive with getItems(). */
  onSelect?: (context: BibleToolContext) => void;
  /** Optional context-menu items resolver. Mutually exclusive with onSelect(). */
  getItems?: (context: BibleToolContext) => ManagedBibleToolbarToolItem[];

  /**
   * Whether the label for this tool should be hidden.
   * Defaults to false.
   */
  hideLabel?: boolean;
}

/** Fully resolved verse toolbar tool ready for rendering. */
export interface BibleReaderVerseToolbarTool extends ResolvedBibleTool {
  /** Disabled state signal resolved for current context. */
  disabled: ReadonlySignal<boolean>;
  /** Visibility state signal resolved for current context. */
  visible: ReadonlySignal<boolean>;
  /** Invoked when the user activates the tool. */
  onSelect: () => void;
  /** Optional context-menu items for this tool. */
  getItems?: () => ResolvedBibleToolItem[];
}

export type ManagedBibleVerseToolbarToolItem =
  ManagedBibleToolItem<BibleToolContext>;

/** Registerable verse toolbar tool definition. */
export interface ManagedBibleVerseToolbarTool extends BibleTool<BibleToolContext> {
  /** Optional disabled predicate (boolean or signal). */
  isDisabled?: ToolPredicate<BibleToolContext>;
  /** Optional visibility predicate (boolean or signal). */
  isVisible?: ToolPredicate<BibleToolContext>;
  /** Optional action callback for tool activation. Mutually exclusive with getItems(). */
  onSelect?: (context: BibleToolContext) => void;
  /** Optional context-menu items resolver. Mutually exclusive with onSelect(). */
  getItems?: (context: BibleToolContext) => ManagedBibleVerseToolbarToolItem[];
}

/** Runtime context passed to empty-slot tool surface. */
export interface EmptySlotToolContext {
  /** Bible selector state for opening in empty slots. */
  selectorState: BibleSelectorState;
  /** Slot currently receiving empty-slot actions. */
  currentSlot: TabSlot;
  /** Tabs layout manager for slot-level operations. */
  tabsLayoutManager: TabsLayoutManager;
  /** Tabs manager for cross-tab interactions. */
  tabs: TabsManager;
  /** Optional window metrics for responsive behavior. */
  window?: WindowContext | null;
}

/** Fully resolved empty-slot tool ready for rendering. */
export interface BibleEmptySlotTool extends ResolvedBibleTool {
  /** Disabled signal resolved for current context. */
  disabled: ReadonlySignal<boolean>;
  /** Visibility signal resolved for current context. */
  visible: ReadonlySignal<boolean>;
  /** Invoked when the user activates the tool. */
  onSelect: () => void;
  /** Optional context-menu items for this tool. */
  getItems?: () => ResolvedBibleToolItem[];
}

export type ManagedBibleEmptySlotToolItem =
  ManagedBibleToolItem<EmptySlotToolContext>;

/** Registerable empty-slot tool definition. */
export interface ManagedBibleEmptySlotTool extends BibleTool<EmptySlotToolContext> {
  /** Optional disabled predicate (boolean or signal). */
  isDisabled?: ToolPredicate<EmptySlotToolContext>;
  /** Optional visibility predicate (boolean or signal). */
  isVisible?: ToolPredicate<EmptySlotToolContext>;
  /** Optional action callback for tool activation. Mutually exclusive with getItems(). */
  onSelect?: (context: EmptySlotToolContext) => void;
  /** Optional context-menu items resolver. Mutually exclusive with onSelect(). */
  getItems?: (context: EmptySlotToolContext) => ManagedBibleEmptySlotToolItem[];
}

/** Fully resolved below-reader tool ready for rendering. */
export interface BibleBelowReaderToolbarTool extends ResolvedBibleTool {
  /** Disabled signal resolved for current context. */
  disabled: ReadonlySignal<boolean>;
  /** Visibility signal resolved for current context. */
  visible: ReadonlySignal<boolean>;
  /** Invoked when the user activates the tool. */
  onSelect: () => void;
  /** Optional context-menu items for this tool. */
  getItems?: () => ResolvedBibleToolItem[];
}

/** Runtime context for below-reader tool surface. */
export interface BibleBelowReaderToolContext extends BibleToolContext {
  /** Slot containing the active reader. */
  currentSlot: TabSlot;
}

/** Registerable below-reader tool definition. */
export interface ManagedBibleBelowReaderToolbarTool extends BibleTool<BibleBelowReaderToolContext> {
  /** Optional disabled predicate (boolean or signal). */
  isDisabled?: ToolPredicate<BibleBelowReaderToolContext>;
  /** Optional visibility predicate (boolean or signal). */
  isVisible?: ToolPredicate<BibleBelowReaderToolContext>;
  /** Optional action callback for tool activation. Mutually exclusive with getItems(). */
  onSelect?: (context: BibleBelowReaderToolContext) => void;
  /** Optional context-menu items resolver. Mutually exclusive with onSelect(). */
  getItems?: (
    context: BibleBelowReaderToolContext
  ) => ManagedBibleBelowReaderToolbarToolItem[];
}

export type ManagedBibleBelowReaderToolbarToolItem =
  ManagedBibleToolItem<BibleBelowReaderToolContext>;

/**
 * Runtime context for the quick toolbar surface — the compact row of
 * actions shown at the top of the reader, beside the chapter bookmark
 * button. Intentionally lean: quick tools are header-level chapter actions
 * and only need the active reading state (plus whichever manager a specific
 * tool's visibility/action depends on).
 */
export interface QuickToolContext {
  /** Active reading state for the current reader surface. */
  readingState: BibleReadingState;

  /**
   * Playlist manager state.
   */
  playlists: PlaylistManager;

  /** Used by the discover-content-panel tool to factor notes into visibility. */
  annotations: AnnotationsManager;

  features: FeaturesManager;

  /** Optional window metrics for responsive tool behavior. */
  window?: WindowContext | null;

  /** Which surface is asking, for tools whose visibility depends on it. */
  surface: "quick-toolbar" | "mobile-navigation-bar";

  /**
   * The current shared bible reading session, if any. Needed by Share so the
   * sheet can offer session actions for the surface being read, not a
   * background tab.
   */
  sharedSession?: BibleReadingSession | null;

  /** Shows a transient toast message. Optional; Share no-ops the copy toast if missing. */
  toast?: (message: string) => void;

  /** Modals manager. Share is hidden when this is absent. */
  modals?: ModalManager;

  /**
   * App-level state. Share is hidden when this is absent, matching the main
   * toolbar tool.
   */
  app?: AppState;
}

/** Fully resolved quick toolbar tool ready for rendering. */
export interface BibleQuickToolbarTool extends ResolvedBibleTool {
  /** Optional class name for custom styling. */
  className?: string;
  /** Disabled signal resolved for current context. */
  disabled: ReadonlySignal<boolean>;
  /** Visibility signal resolved for current context. */
  visible: ReadonlySignal<boolean>;
  /** Invoked when the user activates the tool. */
  onSelect: () => void;
  /** Optional context-menu items for this tool. */
  getItems?: () => ResolvedBibleToolItem[];
}

export type ManagedBibleQuickToolbarToolItem =
  ManagedBibleToolItem<QuickToolContext>;

/** Registerable quick toolbar tool definition. */
export interface ManagedBibleQuickToolbarTool extends BibleTool<QuickToolContext> {
  /** Optional class name for custom styling. */
  className?: string;

  /** Optional disabled predicate (boolean or signal). */
  isDisabled?: ToolPredicate<QuickToolContext>;
  /** Optional visibility predicate (boolean or signal). */
  isVisible?: ToolPredicate<QuickToolContext>;
  /** Optional action callback for tool activation. Mutually exclusive with getItems(). */
  onSelect?: (context: QuickToolContext) => void;
  /** Optional context-menu items resolver. Mutually exclusive with onSelect(). */
  getItems?: (context: QuickToolContext) => ManagedBibleQuickToolbarToolItem[];
}

function validateToolActions(
  tool:
    | ManagedBibleToolbarTool
    | ManagedBibleVerseToolbarTool
    | ManagedBibleEmptySlotTool
    | ManagedBibleBelowReaderToolbarTool
    | ManagedBibleQuickToolbarTool
) {
  if (tool.onSelect && tool.getItems) {
    throw new Error(
      `Tool \"${tool.id}\" cannot define both onSelect() and getItems().`
    );
  }
}

function resolveToolItems<TContext>(
  getItems:
    | ((context: TContext) => ManagedBibleToolItem<TContext>[])
    | undefined,
  context: TContext,
  parentToolId: string
) {
  if (!getItems) {
    return undefined;
  }

  return () =>
    getItems(context).map((item) => {
      const maybeNestedItems = item as ManagedBibleToolItem<TContext> & {
        getItems?: unknown;
      };
      if (maybeNestedItems.getItems) {
        throw new Error(
          `Tool item \"${item.id}\" in \"${parentToolId}\" cannot define getItems().`
        );
      }

      return {
        id: item.id,
        title: item.title,
        icon: () => item.icon(context),
        disabled: resolveToolPredicate(item.isDisabled, context, false),
        visible: resolveToolPredicate(item.isVisible, context, true),
        onSelect: () => item.onSelect?.(context),
      };
    });
}

function resolveToolPredicate<TContext>(
  predicate: ToolPredicate<TContext> | undefined,
  context: TContext,
  defaultValue: boolean
): ReadonlySignal<boolean> {
  const result = predicate?.(context);

  if (typeof result === "undefined") {
    return computed(() => defaultValue);
  }

  if (typeof result === "boolean") {
    return computed(() => result);
  }

  return result;
}

function resolveToolPriority<TContext>(
  priority: ToolPriority<TContext>,
  context: TContext
): number {
  if (typeof priority === "number") {
    return priority;
  }

  return priority(context);
}

function MenuIcon() {
  return <MaterialIcon>menu</MaterialIcon>;
}

function ChevronLeftIcon() {
  return <MaterialIcon>chevron_left</MaterialIcon>;
}

function OpenSelectorIcon() {
  return <SeedBibleIcon size={28} className="sb-open-selector-icon" />;
}

function ChevronRightIcon() {
  return <MaterialIcon>chevron_right</MaterialIcon>;
}

function NextItemIcon() {
  return <MaterialIcon>skip_next</MaterialIcon>;
}

function PreviousItemIcon() {
  return <MaterialIcon>skip_previous</MaterialIcon>;
}

function CopyVerseIcon() {
  return <MaterialIcon>content_copy</MaterialIcon>;
}

function ShareVerseIcon() {
  return <MaterialIcon>share</MaterialIcon>;
}

function ClearSelectionIcon() {
  return <MaterialIcon>clear</MaterialIcon>;
}

function AskAiIcon() {
  return <AskIcon />;
}

/**
 * Most recent local (non-shared) chat that already includes this AI provider.
 * Shared/remote chats are skipped so asking about verses stays a personal
 * conversation. Returns null when none exists so the caller can create one.
 */
function findLocalChatForProvider(chats: ChatsManager, providerId: string) {
  const sessions = chats.chats?.value ?? [];
  for (let i = sessions.length - 1; i >= 0; i--) {
    const chat = sessions[i];
    if (!chat) {
      continue;
    }
    const participants = chat.participants?.value;
    if (
      !participants ||
      participants.some((participant) => participant.isRemote)
    ) {
      continue;
    }
    if (
      participants.some(
        (participant) =>
          participant.isAI && participant.providerId === providerId
      )
    ) {
      return chat;
    }
  }
  return null;
}

/**
 * Opens (or reuses) a local chat with `providerId`, prefills the compose field
 * with the selected verses plus two trailing newlines, then dismisses the
 * verse selection. Reuses an existing thread with that agent when possible so
 * follow-up questions keep conversation context. No-ops when there is nothing
 * to ask about, the provider is gone, or a chat cannot be created.
 */
function openAskAiForSelectedVerses(
  context: BibleToolContext,
  providerId: string
) {
  if (context.readingState.selectedVerses.value.length === 0) {
    return;
  }

  const provider = context.chats.providers.value.find(
    (entry) => entry.id === providerId
  );
  if (!provider) {
    return;
  }

  const verseText = formatSelectedVerses(context.readingState);
  if (context.chats.composerDraft) {
    context.chats.composerDraft.value = verseText ? `${verseText}\n\n` : "";
  }

  const existingChat = findLocalChatForProvider(context.chats, providerId);
  const chat = existingChat ?? context.chats.createLocalSession?.();
  if (!chat) {
    return;
  }
  chat.addParticipant(providerId);
  context.chats.selectChat(chat.id);
  context.openChat?.();
  // Clearing the selection unmounts the mobile verse sheet under the finger.
  // Defer so a retargeted pointerdown after that unmount cannot land "outside"
  // the chat panel and dismiss the panel we just opened.
  queueMicrotask(() => {
    context.readingState.clearSelectedVerses();
  });
}

function OpenInSelectorIcon() {
  return <MaterialIcon>menu_book</MaterialIcon>;
}

function getDefaultEmptySlotToolbarTools(): ManagedBibleEmptySlotTool[] {
  return [
    {
      id: "open-in-selector",
      priority: 0,
      title: { key: "books", defaultValue: "Books" },
      icon: OpenInSelectorIcon,
      onSelect: (context) => {
        context.selectorState.setOpen(true, context.currentSlot);
      },
    },
  ];
}

function getDefaultBelowReaderToolbarTools(): ManagedBibleBelowReaderToolbarTool[] {
  return [
    {
      id: "powered-by",
      priority: 0,
      title: "Powered by Seed Bible API",
      icon: () => (
        <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
          <SeedBibleIcon />
        </span>
      ),
      isDisabled: () => true,
      onSelect: () => {},
    },
  ];
}

function NowPlayingIcon({
  playlists,
  readingState,
}: {
  playlists: PlaylistManager;
  readingState: BibleReadingState;
}) {
  const { t } = useI18n();
  const currentlyPlaying = playlists.playing.value;
  if (!currentlyPlaying) return null;
  const currentPlaylist = currentlyPlaying.playlists.value[0];
  const currentItem = currentlyPlaying.currentItem.value;

  const books = readingState.translationBooks.value;
  const resolveBookName = (bookId: string): string => {
    const book = books?.books.find((b) => b.id === bookId);
    return book?.name ?? book?.commonName ?? bookId;
  };

  const title =
    currentPlaylist?.title ??
    t("untitled-playlist", { defaultValue: "Untitled playlist" });
  return (
    <span className="sb-now-playing-icon">
      <h4 className="sb-now-playing-icon-title">{title}</h4>
      {currentItem && (
        <span>{playlistItemLabel(currentItem, t, resolveBookName)}</span>
      )}
    </span>
  );
}

function getDefaultQuickToolbarTools(
  branding?: BrandingConfig
): ManagedBibleQuickToolbarTool[] {
  const tools: ManagedBibleQuickToolbarTool[] = [
    {
      id: "current-playlist",
      priority: 0,
      title: {
        key: "current-playlist",
        defaultValue: "Current Playlist",
      },
      className: "sb-quick-toolbar-current-playlist",
      icon: (c) => (
        <NowPlayingIcon playlists={c.playlists} readingState={c.readingState} />
      ),
      isVisible: (c) =>
        !!c.playlists.playing.value?.playlists.value.length &&
        c.playlists.isMobile.value,
      onSelect: (c) => {
        c.playlists.view.value = "play_playlist";
      },
    },
    {
      id: "discover-content-panel",
      priority: 10,
      title: {
        key: "discover-content-panel",
        defaultValue: "Discover content",
      },
      icon: (c) => (
        <MaterialIcon
          className={
            c.readingState.discoverContentPanelInline.value
              ? "sb-quick-tool-icon-active"
              : undefined
          }
        >
          explore
        </MaterialIcon>
      ),
      isVisible: (c) => {
        if (c.app?.isMobile?.value) {
          return false;
        }
        if (hasAnyDiscoverResults(c.readingState)) {
          return true;
        }
        const bookId = c.readingState.bookId.value;
        const chapterNumber = c.readingState.chapterNumber.value;
        if (!bookId || !chapterNumber) {
          return false;
        }
        return (
          c.annotations.getAnnotationsForChapter(bookId, chapterNumber).value
            .length > 0
        );
      },
      onSelect: (c) => {
        c.readingState.discoverContentPanelInline.value =
          !c.readingState.discoverContentPanelInline.value;
      },
    },
    {
      id: "share",
      priority: 100,
      title: { key: "share", defaultValue: "Share" },
      icon: () => <MaterialIcon>share</MaterialIcon>,
      // Header-level so Share stays visible instead of sitting in the bottom
      // toolbar (desktop) or More menu (mobile). Hidden on the
      // mobile-navigation-bar surface, which is reserved for the audio play
      // control.
      isVisible: (context) =>
        computed(
          () =>
            context.surface === "quick-toolbar" &&
            !!context.modals &&
            !!context.app
        ),
      onSelect: (context) => {
        openShareModal(context, getShareUrl(context.readingState));
      },
    },
  ];
  return tools.filter(
    (tool) => !branding?.disabledToolbarTools?.includes(tool.id)
  );
}

function getDefaultToolbarTools(
  branding?: BrandingConfig
): ManagedBibleToolbarTool[] {
  const tools: ManagedBibleToolbarTool[] = [
    {
      id: "stop-playing",
      priority: -1,
      hideLabel: true,
      title: { key: "stop", defaultValue: "Stop" },
      icon: () => <StopIcon />,
      isVisible: (context) => !!context.playlists?.playing?.value,
      onSelect: (context) => {
        context.playlists?.stopPlaying();
      },
      isControllable: false,
    },
    {
      id: "previous-chapter",
      priority: 0,
      hideLabel: true,
      title: { key: "previous-chapter", defaultValue: "Previous Chapter" },
      icon: (context) =>
        context.readingState.translation.value?.textDirection === "rtl" ? (
          <ChevronRightIcon />
        ) : (
          <ChevronLeftIcon />
        ),
      // Deliberately not gated on `loading`: navigation no longer waits on the
      // text request, so pressing again mid-load is exactly what should work.
      isDisabled: (context) => !context.readingState.hasPrevious.value,
      isVisible: (context) => !context.playlists?.playing?.value,
      onSelect: (context) => {
        context.readingState.loadPreviousChapter();
      },
      isControllable: false,
    },
    {
      id: "previous-item",
      priority: 0,
      hideLabel: true,
      title: { key: "previous", defaultValue: "Previous" },
      icon: (context) =>
        context.readingState.translation.value?.textDirection === "rtl" ? (
          <NextItemIcon />
        ) : (
          <PreviousItemIcon />
        ),
      isDisabled: (context) =>
        !context.playlists?.playing?.value?.hasPrevious.value,
      isVisible: (context) => !!context.playlists?.playing?.value,
      onSelect: (context) => {
        context.playlists?.playing.value?.previous();
      },
      isControllable: false,
    },
    {
      id: "open-sidebar",
      priority: 50,
      title: { key: "side-menu", defaultValue: "Side menu" },
      icon: MenuIcon,
      isVisible: (context) =>
        !!context.openSidebar && (context.window?.isMobile ?? false),
      onSelect: (context) => {
        context.openSidebar?.();
      },
      isControllable: false,
    },
    {
      id: "open-selector",
      priority: 100,
      title: { key: "books", defaultValue: "Books" },
      icon: OpenSelectorIcon,
      onSelect: (context) => {
        const currentSlot =
          context.tabsLayoutManager.slots.value.find(
            (slot) => slot.id === context.tabsLayoutManager.selectedSlotId.value
          ) ?? null;
        if (!currentSlot) {
          return;
        }

        context.selectorState.setOpen(true, currentSlot);
      },
      isControllable: false,
    },
    {
      id: "open-search",
      priority: 110,
      title: { key: "search", defaultValue: "Search" },
      icon: () => <MaterialIcon>search</MaterialIcon>,
      onSelect: (context) => {
        context.openSearch();
      },
    },
    {
      id: "open-chat",
      priority: 120,
      title: { key: "chat", defaultValue: "Chat" },
      icon: () => <MaterialIcon>chat_bubble_outline</MaterialIcon>,
      isVisible: (context) => {
        // Hide when there are no providers and no chats
        return (
          context.chats.providers.value.length > 0 ||
          context.chats.chats.value.length > 0
        );
      },
      onSelect: (context) => {
        context.openChat?.();
      },
    },
    {
      id: "open-plans",
      priority: 115,
      title: { key: "plans", defaultValue: "Plans" },
      icon: () => <MaterialIcon>menu_book</MaterialIcon>,
      isVisible: (context) =>
        !!context.readingPlans &&
        context.features.isFeatureEnabled(FEATURE_KEY_READING_PLANS).value,
      onSelect: (context) => {
        const readingPlans = context.readingPlans;
        if (!readingPlans) {
          return;
        }
        const readingState = context.readingState;
        context.panesManager.openPane({
          id: "reading-plans-pane",
          placement: "side",

          // The pane's own header carries the plans chrome: a back button when
          // the user has drilled into a plan or the create wizard, the plan's
          // name as the title, and the new-plan button.
          title: () => <ReadingPlansPaneTitle readingPlans={readingPlans} />,
          icon: () => <ReadingPlansPaneIcon />,
          leading: () => (
            <ReadingPlansPaneLeading readingPlans={readingPlans} />
          ),
          header: () => <ReadingPlansPaneActions readingPlans={readingPlans} />,
          component: () => (
            <ReadingPlansPane
              readingPlans={readingPlans}
              books={readingState.translationBooks.value?.books ?? []}
              modals={context.modals}
              // Tapping a scripture reading takes the user to it. Without this
              // a plan can only be ticked off, never actually read from.
              onOpenScripture={async (ref, translationId) => {
                await readingState.selectTranslationAndChapter(
                  translationId ?? readingState.translationId.peek(),
                  ref.bookId,
                  ref.chapter,
                  { scrollToVerse: ref.verse }
                );
              }}
              // A day of a plan is a run of readings, which is exactly what the
              // playlist queue already steps through — so it is handed straight
              // to `startPlaying` rather than growing a second set of next/back
              // controls here. The synthetic playlist borrows the plan's own
              // record name and address so playback is identifiable; it isn't a
              // real playlist record, so a shared/reloaded URL won't resume it.
              onPlayReadings={(plan, items, startIndex) => {
                context.playlists?.startPlaying(
                  {
                    id: plan.address,
                    // recordName: plan.recordName,
                    // authorUserId: plan.authorUserId,
                    title: plan.title,
                    description: plan.description,
                    items,
                    // createdAtMs: plan.createdAtMs,
                    // updatedAtMs: plan.updatedAtMs,
                  },
                  startIndex,
                  // Reading plans reuse the playlist player but keep their own
                  // progress records — don't also write playlist play history.
                  { history: false }
                );
              }}
            />
          ),
        });
      },
    },
    {
      id: "open-discover",
      priority: 120,
      title: { key: "discover", defaultValue: "Discover" },
      icon: () => <MaterialIcon>explore</MaterialIcon>,
      isVisible: (context) => !!context.openDiscover,
      onSelect: (context) => {
        if (!context.openDiscover) {
          return;
        }
        context.openDiscover();
      },
    },
    {
      id: "next-chapter",
      priority: 1000,
      hideLabel: true,
      title: { key: "next-chapter", defaultValue: "Next Chapter" },
      icon: (context) =>
        context.readingState.translation.value?.textDirection === "rtl" ? (
          <ChevronLeftIcon />
        ) : (
          <ChevronRightIcon />
        ),
      // See `previous-chapter`: in-flight text must not block moving on.
      isDisabled: (context) => !context.readingState.hasNext.value,
      isVisible: (context) => !context.playlists?.playing?.value,
      onSelect: (context) => {
        context.readingState.loadNextChapter();
      },
      isControllable: false,
    },
    {
      id: "next-item",
      priority: 1000,
      hideLabel: true,
      title: { key: "next", defaultValue: "Next" },
      icon: (context) =>
        context.readingState.translation.value?.textDirection === "rtl" ? (
          <PreviousItemIcon />
        ) : (
          <NextItemIcon />
        ),
      isDisabled: (context) =>
        !context.playlists?.playing?.value?.hasNext.value,
      isVisible: (context) => !!context.playlists?.playing?.value,
      onSelect: (context) => {
        context.playlists?.playing.value?.next();
      },
      isControllable: false,
    },
  ];
  return tools.filter(
    (tool) => !branding?.disabledToolbarTools?.includes(tool.id)
  );
}

function getDefaultVerseToolbarTools(): ManagedBibleVerseToolbarTool[] {
  return [
    {
      id: "add-to-playlist",
      priority: 100,
      title: { key: "add-to-playlist", defaultValue: "Add to Playlist" },
      icon: () => <MaterialIcon>playlist_add</MaterialIcon>,
      isVisible: (context) => !!context.playlists?.editingPlaylist.value,
      onSelect: async (context) => {
        const playlist = context.playlists?.editingPlaylist.value;
        if (!playlist) return;

        const chapterData = context.readingState.chapterData.value;
        context.playlists!.editingPlaylist.value = {
          ...playlist,
          items: [
            ...playlist.items,
            ...groupVersesIntoPlaylistItems(
              context.readingState.selectedVerses.value.map((verse) => ({
                bookId: verse.bookId,
                chapter: verse.chapterNumber,
                verse: verse.verse.number,
              })),
              (bookId, chapter) =>
                chapterData?.book.id === bookId &&
                chapterData.chapter.number === chapter
                  ? chapterData.numberOfVerses
                  : undefined
            ),
          ],
        };

        context.readingState.clearSelectedVerses();
      },
    },
    {
      id: "add-to-reading-plan",
      priority: 125,
      title: { key: "add-to-reading-plan", defaultValue: "Add to plan" },
      icon: () => <MaterialIcon>library_add</MaterialIcon>,
      // Only offered while a plan is actually being authored — it adds to that
      // draft, so with no draft there is nowhere for the passage to go. Mirrors
      // how "Add to Playlist" follows `editingPlaylist`.
      isVisible: (context) =>
        !!context.readingPlans?.editingReadingPlan.value &&
        context.features.isFeatureEnabled(FEATURE_KEY_READING_PLANS) &&
        context.readingState.selectedVerses.value.length > 0,
      onSelect: async (context) => {
        const readingPlans = context.readingPlans;
        const draft = readingPlans?.editingReadingPlan.value;
        if (!readingPlans || !draft) {
          return;
        }
        const verses = context.readingState.selectedVerses.value;
        if (verses.length === 0) {
          return;
        }
        // Collapse the selection into one reading spanning its first → last
        // verse. A selection can cross a chapter boundary, so order by
        // (chapter, verse) rather than verse number alone and record the span
        // with endChapter/endVerse.
        const first = verses[0]!;
        const ordered = verses
          .filter((v) => v.bookId === first.bookId)
          .sort(
            (a, b) =>
              a.chapterNumber - b.chapterNumber ||
              a.verse.number - b.verse.number
          );
        const start = ordered[0]!;
        const end = ordered[ordered.length - 1]!;
        const spansChapters = end.chapterNumber !== start.chapterNumber;

        readingPlans.addReadingToEditingPlan({
          type: "bible-verse",
          ref: {
            bookId: start.bookId,
            chapter: start.chapterNumber,
            verse: start.verse.number,
            ...(spansChapters
              ? { endChapter: end.chapterNumber, endVerse: end.verse.number }
              : end.verse.number !== start.verse.number
                ? { endVerse: end.verse.number }
                : {}),
          },
        });
        context.toast(
          i18n.t("added-to-reading-plan-session", {
            defaultValue: "Added to session {{session}}",
            session: draft.selectedSessionIndex + 1,
          })
        );
        context.readingState.clearSelectedVerses();
      },
    },
    {
      id: "annotate-verse",
      priority: 150,
      title: { key: "note", defaultValue: "Note" },
      icon: () => <MaterialIcon>note_add</MaterialIcon>,
      isVisible: (context) =>
        !!context.annotations &&
        context.readingState.selectedVerses.value.length > 0,
      onSelect: async (context) => {
        if (!context.annotations) return;
        await context.annotations.createNewAnnotation();
      },
    },
    {
      id: "ask-ai",
      priority: 80,
      title: { key: "ask-ai", defaultValue: "Ask AI" },
      icon: AskAiIcon,
      isVisible: (context) =>
        context.chats.providers.value.length > 0 &&
        context.readingState.selectedVerses.value.length > 0,
      // Single provider: getItems is empty so the verse toolbar calls onSelect
      // and opens that agent immediately. Multiple providers: getItems fills
      // the picker. Default tools are not run through validateToolActions,
      // which otherwise forbids defining both.
      getItems: (context) => {
        const providers = context.chats.providers.value;
        if (providers.length <= 1) {
          return [];
        }
        return providers.map((provider) => ({
          id: `ask-ai-${provider.id}`,
          title: provider.name,
          icon: AskAiIcon,
          onSelect: () => openAskAiForSelectedVerses(context, provider.id),
        }));
      },
      onSelect: (context) => {
        const providers = context.chats.providers.value;
        if (providers.length !== 1) {
          return;
        }
        const provider = providers[0];
        if (!provider) {
          return;
        }
        openAskAiForSelectedVerses(context, provider.id);
      },
    },
    {
      id: "copy-verse",
      priority: 200,
      title: { key: "copy-verse", defaultValue: "Copy" },
      icon: CopyVerseIcon,
      isVisible: (context) =>
        context.readingState.selectedVerses.value.length > 0,
      onSelect: async (context) => {
        if (context.readingState.selectedVerses.value.length === 0) return;

        const verseTexts = formatSelectedVerses(context.readingState);

        try {
          navigator.clipboard.writeText(verseTexts);
          context.toast(i18n.t("copied", { defaultValue: "Copied" }));
        } catch (err) {
          console.error("Failed to copy verse:", err);
        }
      },
    },
    {
      id: "share-verse",
      priority: 300,
      title: { key: "share-verse", defaultValue: "Share" },
      icon: ShareVerseIcon,
      isVisible: (context) =>
        context.readingState.selectedVerses.value.length > 0,
      onSelect: (context) => {
        if (context.readingState.selectedVerses.value.length === 0) return;
        openShareModal(
          context,
          getShareUrl(context.readingState),
          formatSelectedVerses(context.readingState)
        );
      },
    },
    {
      id: "clear-selection",
      priority: 400,
      title: { key: "cancel", defaultValue: "Cancel" },
      icon: ClearSelectionIcon,
      isVisible: (context) =>
        context.readingState.selectedVerses.value.length > 0,
      onSelect: (context) => {
        context.readingState.clearSelectedVerses();
      },
    },
  ];
}

/**
 * Lightweight tool descriptor used for introspection (e.g. settings UI)
 * where we need to list tools without a full rendering context.
 */
export interface ToolMetadata {
  id: string;
  title: TranslatableTitle;
  isControllable?: boolean;
}

/**
 * API surface for registering and resolving tools across all reader toolbars.
 */
export interface ToolsManager {
  /** Registers a reader toolbar tool and returns an unregister callback. */
  registerToolbarTool: (tool: ManagedBibleToolbarTool) => () => void;

  /** Unregisters a reader toolbar tool by ID. */
  unregisterToolbarTool: (toolId: string) => void;

  /** Resolves/sorts reader toolbar tools for the given context. */
  getToolbarTools: (context: BibleToolContext) => BibleReaderToolbarTool[];

  /** Lists reader toolbar tool metadata without resolving any context. */
  listToolbarTools: () => ToolMetadata[];

  /** Registers a verse toolbar tool and returns an unregister callback. */
  registerVerseToolbarTool: (tool: ManagedBibleVerseToolbarTool) => () => void;

  /** Unregisters a verse toolbar tool by ID. */
  unregisterVerseToolbarTool: (toolId: string) => void;

  /** Resolves/sorts verse toolbar tools for the given context. */
  getVerseToolbarTools: (
    context: BibleToolContext
  ) => BibleReaderVerseToolbarTool[];

  /** Lists verse toolbar tool metadata without resolving any context. */
  listVerseToolbarTools: () => ToolMetadata[];

  /** Registers an empty-slot tool and returns an unregister callback. */
  registerEmptySlotTool: (tool: ManagedBibleEmptySlotTool) => () => void;

  /** Unregisters an empty-slot tool by ID. */
  unregisterEmptySlotTool: (toolId: string) => void;

  /** Resolves/sorts empty-slot tools for the given context. */
  getEmptySlotTools: (context: EmptySlotToolContext) => BibleEmptySlotTool[];

  /** Registers a below-reader tool and returns an unregister callback. */
  registerBelowReaderTool: (
    tool: ManagedBibleBelowReaderToolbarTool
  ) => () => void;

  /** Unregisters a below-reader tool by ID. */
  unregisterBelowReaderTool: (toolId: string) => void;

  /** Resolves/sorts below-reader tools for the given context. */
  getBelowReaderTools: (
    context: BibleBelowReaderToolContext
  ) => BibleBelowReaderToolbarTool[];

  /** Registers a quick toolbar tool and returns an unregister callback. */
  registerQuickTool: (tool: ManagedBibleQuickToolbarTool) => () => void;

  /** Unregisters a quick toolbar tool by ID. */
  unregisterQuickTool: (toolId: string) => void;

  /** Resolves/sorts quick toolbar tools for the given context. */
  getQuickTools: (context: QuickToolContext) => BibleQuickToolbarTool[];

  /** Lists quick toolbar tool metadata without resolving any context. */
  listQuickTools: () => ToolMetadata[];
}

/**
 * Generates a sharable URL for the given reading state.
 * @param readingState The Bible reading state to generate the URL from.
 * @returns A URL object representing the sharable link for the current reading state.
 */
export function getShareUrl(readingState: BibleReadingState) {
  const current = new URL(window.location.href);
  current.search = "";
  // if (configBot.tags.pattern) {
  //   url.searchParams.set("pattern", configBot.tags.pattern);
  // }
  const translation =
    readingState.translation.value?.id ?? readingState.defaultTranslation.id;
  const bookId = readingState.bookId.value ?? DEFAULT_BOOK_ID;
  const chapter = readingState.chapterNumber.value;
  const { basePath } = readInjectedConfig();
  // Written into the path rather than as `?translation=&book=&chapter=`, so a
  // shared link is the canonical URL for the passage instead of one that
  // redirects to it.
  const url = buildReadingUrl({
    currentUrl: current,
    basePath,
    translationId: translation,
    bookId: bookId as BookId,
    chapter,
    fallbackLanguage: uiLocaleForDefaultTranslation(translation) ?? undefined,
  });

  if (readingState.selectedVerses.value.length > 0) {
    const verses = readingState.selectedVerses.value
      .filter(
        (v) =>
          v.bookId === bookId &&
          v.chapterNumber === chapter &&
          v.translationId === translation
      )
      .map((v) => v.verse.number);
    if (verses.length > 0) {
      const formatted = formatVerseSelection(verses);
      if (formatted) {
        url.searchParams.set("verse", formatted);
      }
    }
  }
  return url;
}

/**
 * Fields `openShareModal` actually reads. Both the main/verse toolbar context
 * and the leaner quick-toolbar context satisfy this, so Share can live on
 * either surface without forcing quick tools to carry the full toolbar context.
 */
type ShareSheetContext = {
  modals?: ModalManager;
  app?: AppState;
  toast?: (message: string) => void;
  sharedSession?: BibleReadingSession | null;
};

/**
 * Opens the unified share sheet for a reading surface. Shared by the verse
 * toolbar's "Share" tool, the reader toolbar's "Share" tool, the mobile
 * quick-toolbar "Share" tool, and the tabs-screen Share control so every
 * surface opens the exact same modal. `shareText` is only passed by the verse
 * flow (the selected verses' text) for the native share sheet; the reader
 * flow shares a link only. The session comes from `context.sharedSession` —
 * the tool's own reading surface — never from global app state, so a
 * background surface can't be shared by mistake.
 */
export function openShareModal(
  context: ShareSheetContext,
  shareUrl: URL,
  shareText?: string
) {
  const modals = context.modals;
  const app = context.app;
  if (!modals || !app) return;

  const modalId = modals.openModal({
    title: { key: "share-sheet-title", defaultValue: "Share" },
    content: () => (
      <ShareModal
        app={app}
        session={context.sharedSession ?? null}
        onClose={() => modals.closeModal(modalId)}
        onShareLink={() => {
          navigator.clipboard.writeText(shareUrl.toString());
          context.toast?.(i18n.t("copied", { defaultValue: "Copied" }));
          modals.closeModal(modalId);
        }}
        onShareVia={() => {
          void navigator.share?.({
            title: document.title,
            ...(shareText ? { text: shareText } : {}),
            url: shareUrl.toString(),
          });
          modals.closeModal(modalId);
        }}
      />
    ),
  });
}

/** Splits verses into groups of consecutive verse numbers. */
function groupConsecutiveVerses(verses: BibleSelectedVerse[]) {
  const groups: BibleSelectedVerse[][] = [];
  let current: BibleSelectedVerse[] = [];

  for (const verse of verses) {
    if (
      current.length === 0 ||
      verse.verse.number === current[current.length - 1]!.verse.number + 1
    ) {
      current.push(verse);
    } else {
      groups.push(current);
      current = [verse];
    }
  }

  if (current.length) {
    groups.push(current);
  }

  return groups;
}

/**
 * Formats a single contiguous run of verse numbers, e.g. `12` or `12-17`.
 * Assumes `verseNumbers` is already sorted and contiguous, which holds for
 * every caller since it only ever receives a group from `groupConsecutiveVerses`.
 */
function formatVerseRanges(verseNumbers: number[]): string {
  if (verseNumbers.length === 0) return "";

  const start = verseNumbers[0]!;
  const end = verseNumbers[verseNumbers.length - 1]!;

  return start === end ? `${start}` : `${start}-${end}`;
}

/**
 * Formats the selected verses from the reading state into a human-readable string.
 * @param readingState The reading state containing the selected verses to format.
 * @returns A string representing the formatted selected verses.
 */
export function formatSelectedVerses(readingState: BibleReadingState) {
  const verses = readingState.selectedVerses.value;

  if (verses.length === 0) return "";

  const bookName =
    readingState.chapterData.value?.book.name ?? verses[0]!.bookId;

  const translation = readingState.translation?.value?.shortName ?? "";

  const groups = groupConsecutiveVerses(verses);

  return groups
    .map((group) => {
      const text = group
        .map((verse) => extractContentText(verse.verse.content))
        .join(" ");

      const range = formatVerseRanges(group.map((v) => v.verse.number));

      const reference = [
        bookName,
        `${group[0]!.chapterNumber}:${range}`,
        translation,
      ]
        .filter(Boolean)
        .join(" ");

      return `${text} (${reference})`;
    })
    .join("\n\n");
}

/**
 * Creates the tools manager with default tool sets and registration APIs.
 *
 * Notes:
 * - Registering a tool with an existing ID replaces the previous definition.
 * - Getter methods resolve predicates and priorities for the provided context,
 *   then return tools sorted by ascending priority.
 */
export function createBibleToolsManager(
  branding?: BrandingConfig
): ToolsManager {
  const toolbarTools = signal<ManagedBibleToolbarTool[]>(
    getDefaultToolbarTools(branding)
  );
  const verseToolbarTools = signal<ManagedBibleVerseToolbarTool[]>(
    getDefaultVerseToolbarTools()
  );
  const emptySlotTools = signal<ManagedBibleEmptySlotTool[]>(
    getDefaultEmptySlotToolbarTools()
  );
  const belowReaderTools = signal<ManagedBibleBelowReaderToolbarTool[]>(
    getDefaultBelowReaderToolbarTools()
  );
  const quickTools = signal<ManagedBibleQuickToolbarTool[]>(
    getDefaultQuickToolbarTools(branding)
  );

  const registerToolbarTool = (tool: ManagedBibleToolbarTool) => {
    validateToolActions(tool);
    const nextTools = toolbarTools.value.filter(
      (entry) => entry.id !== tool.id
    );
    toolbarTools.value = [...nextTools, tool];

    return () => {
      unregisterToolbarTool(tool.id);
    };
  };

  const unregisterToolbarTool = (toolId: string) => {
    toolbarTools.value = toolbarTools.value.filter(
      (tool) => tool.id !== toolId
    );
  };

  const getToolbarTools = (context: BibleToolContext) => {
    const tools = toolbarTools.value.map((tool) => ({
      id: tool.id,
      priority: resolveToolPriority(tool.priority, context),
      title: tool.title,
      icon: () => tool.icon(context),
      disabled: resolveToolPredicate(tool.isDisabled, context, false),
      visible: resolveToolPredicate(tool.isVisible, context, true),
      onSelect: () => tool.onSelect?.(context),
      getItems: resolveToolItems(tool.getItems, context, tool.id),
      isControllable: tool.isControllable ?? true,
      hideLabel: tool.hideLabel ?? false,
    }));

    return sortBy(tools, [(tool) => tool.priority]);
  };

  const listToolbarTools = (): ToolMetadata[] =>
    toolbarTools.value.map((tool) => ({
      id: tool.id,
      title: tool.title,
      isControllable: tool.isControllable ?? true,
    }));

  const registerVerseToolbarTool = (tool: ManagedBibleVerseToolbarTool) => {
    validateToolActions(tool);
    const nextTools = verseToolbarTools.value.filter(
      (entry) => entry.id !== tool.id
    );
    verseToolbarTools.value = [...nextTools, tool];

    return () => {
      unregisterVerseToolbarTool(tool.id);
    };
  };

  const unregisterVerseToolbarTool = (toolId: string) => {
    verseToolbarTools.value = verseToolbarTools.value.filter(
      (tool) => tool.id !== toolId
    );
  };

  const getVerseToolbarTools = (context: BibleToolContext) => {
    const tools = verseToolbarTools.value.map((tool) => ({
      id: tool.id,
      priority: resolveToolPriority(tool.priority, context),
      title: tool.title,
      icon: () => tool.icon(context),
      disabled: resolveToolPredicate(tool.isDisabled, context, false),
      visible: resolveToolPredicate(tool.isVisible, context, true),
      onSelect: () => tool.onSelect?.(context),
      getItems: resolveToolItems(tool.getItems, context, tool.id),
    }));

    return sortBy(tools, [(tool) => tool.priority]);
  };

  const listVerseToolbarTools = (): ToolMetadata[] =>
    verseToolbarTools.value.map((tool) => ({ id: tool.id, title: tool.title }));

  const registerEmptySlotTool = (tool: ManagedBibleEmptySlotTool) => {
    validateToolActions(tool);
    const nextTools = emptySlotTools.value.filter(
      (entry) => entry.id !== tool.id
    );
    emptySlotTools.value = [...nextTools, tool];

    return () => {
      unregisterEmptySlotTool(tool.id);
    };
  };

  const unregisterEmptySlotTool = (toolId: string) => {
    emptySlotTools.value = emptySlotTools.value.filter(
      (tool) => tool.id !== toolId
    );
  };

  const getEmptySlotTools = (context: EmptySlotToolContext) => {
    const tools = emptySlotTools.value.map((tool) => ({
      id: tool.id,
      priority: resolveToolPriority(tool.priority, context),
      title: tool.title,
      icon: () => tool.icon(context),
      disabled: resolveToolPredicate(tool.isDisabled, context, false),
      visible: resolveToolPredicate(tool.isVisible, context, true),
      onSelect: () => tool.onSelect?.(context),
      getItems: resolveToolItems(tool.getItems, context, tool.id),
    }));

    return sortBy(tools, [(tool) => tool.priority]);
  };

  const registerBelowReaderTool = (
    tool: ManagedBibleBelowReaderToolbarTool
  ) => {
    validateToolActions(tool);
    const nextTools = belowReaderTools.value.filter(
      (entry) => entry.id !== tool.id
    );
    belowReaderTools.value = [...nextTools, tool];

    return () => {
      unregisterBelowReaderTool(tool.id);
    };
  };

  const unregisterBelowReaderTool = (toolId: string) => {
    belowReaderTools.value = belowReaderTools.value.filter(
      (tool) => tool.id !== toolId
    );
  };

  const getBelowReaderTools = (context: BibleBelowReaderToolContext) => {
    const tools = belowReaderTools.value.map((tool) => ({
      id: tool.id,
      priority: resolveToolPriority(tool.priority, context),
      title: tool.title,
      icon: () => tool.icon(context),
      disabled: resolveToolPredicate(tool.isDisabled, context, false),
      visible: resolveToolPredicate(tool.isVisible, context, true),
      onSelect: () => tool.onSelect?.(context),
      getItems: resolveToolItems(tool.getItems, context, tool.id),
    }));

    return sortBy(tools, [(tool) => tool.priority]);
  };

  const registerQuickTool = (tool: ManagedBibleQuickToolbarTool) => {
    validateToolActions(tool);
    const nextTools = quickTools.value.filter((entry) => entry.id !== tool.id);
    quickTools.value = [...nextTools, tool];

    return () => {
      unregisterQuickTool(tool.id);
    };
  };

  const unregisterQuickTool = (toolId: string) => {
    quickTools.value = quickTools.value.filter((tool) => tool.id !== toolId);
  };

  const getQuickTools = (context: QuickToolContext) => {
    const tools = quickTools.value.map((tool) => ({
      id: tool.id,
      priority: resolveToolPriority(tool.priority, context),
      title: tool.title,
      icon: () => tool.icon(context),
      disabled: resolveToolPredicate(tool.isDisabled, context, false),
      visible: resolveToolPredicate(tool.isVisible, context, true),
      onSelect: () => tool.onSelect?.(context),
      getItems: resolveToolItems(tool.getItems, context, tool.id),
      className: tool.className,
    }));

    return sortBy(tools, [(tool) => tool.priority]);
  };

  const listQuickTools = (): ToolMetadata[] =>
    quickTools.value.map((tool) => ({ id: tool.id, title: tool.title }));

  return {
    registerToolbarTool,
    unregisterToolbarTool,
    getToolbarTools,
    listToolbarTools,
    registerVerseToolbarTool,
    unregisterVerseToolbarTool,
    getVerseToolbarTools,
    listVerseToolbarTools,
    registerEmptySlotTool,
    unregisterEmptySlotTool,
    getEmptySlotTools,
    registerBelowReaderTool,
    unregisterBelowReaderTool,
    getBelowReaderTools,
    registerQuickTool,
    unregisterQuickTool,
    getQuickTools,
    listQuickTools,
  };
}
